import { LeftOutlined } from "@ant-design/icons";
import { Col, Flex, Row } from "antd";
import RadioButton from "components/RadioButton";
import Button from "components/button/Button";
import CKEditorCustom from "components/ckeditor";
import DatePickerComponent from "components/datepicker/Datepicker";
import InputComponent from "components/formInput/Input";
import Loading from "components/loading/Loading";
import PopupConfirm from "components/popup-confirm";
import { IMAGE_MAX_SIZE, IMAGE_TYPE_UPLOAD } from "constants/Constants";
import { HASHTAG_REGEX, HTMLTAG_REGEX } from "constants/regex";
import { useFormik } from "formik";
import { apiUploadImage } from "helpers/api/upload-image";
import useAppDispatch from "hooks/useAppDispatch";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { showToastError } from "utils";
import { ARTICLE_LIST_OPTIONS_VALUE } from "../constants";
import { validationSchema } from "./schema";
import "./styles.scss";
import dayjs from "dayjs";
import { calc } from "antd/es/theme/internal";

const DEFAULT_DATA = {
  subject: "",
  content: "",
  thumbnail: "",
  public: ARTICLE_LIST_OPTIONS_VALUE.PUBLISH,
  date: "",
  time: "",
  content_no_html: "",
};

const ArticleForm = () => {
  const { t } = useTranslation();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const history = useHistory();
  const { id } = useParams();

  const isEdit = !!id;

  const imgRef = useRef();
  const {
    data: {
      detailArticles: { data, isLoading },
      createArticles: { isLoading: isLoadingCreate },
      updateArticles: { isLoading: isLoadingUpdate },
      updateStatus: { isLoading: isLoadingUpdateStatus },
    },
    actions: {
      actUpdateStatusArticles,
      actCreateArticles,
      actGetDetailArticles,
      actGetDetailArticlesReset,
      actUpdateArticles,
    },
  } = useAppDispatch("articles");

  useEffect(() => {
    if (id) {
      actGetDetailArticles(id);
    }
    return () => {
      actGetDetailArticlesReset();
    };
  }, [id]);

  const backToList = () => {
    history.push("/admin/articles");
  };

  const onSubmit = ({ date, time, content_no_html, ...values }) => {
    const release_date = dayjs(`${dayjs(date).format("YYYY-MM-DD")} ${dayjs(time).format("HH:mm:ss")}`).utc().format();
    const body = {
      ...values,
      release_date,
      content_no_html,
      public:
        values?.public === ARTICLE_LIST_OPTIONS_VALUE.PUBLISH ? true : false,
    };

    body.hashtags = content_no_html?.match(HASHTAG_REGEX) || [];

    if (isEdit) {
      actUpdateArticles(
        {
          ...body,
          id,
        },
        backToList
      );
    } else {
      actCreateArticles(body, backToList);
    }
  };
  const initialValues = useMemo(() => {
    if (data && isEdit) {
      return {
        subject: data.subject,
        content: data.content,
        content_no_html: data.content_no_html,
        thumbnail: data.thumbnail,
        public: data.public
          ? ARTICLE_LIST_OPTIONS_VALUE.PUBLISH
          : ARTICLE_LIST_OPTIONS_VALUE.HIDDEN,
        date: data.release_date ? new Date(data.release_date) : "",
        time: data.release_date ? new Date(data.release_date) : "",
      };
    }
    return DEFAULT_DATA;
  }, [data]);

  const { values, errors, handleChange, setFieldValue, handleSubmit } =
    useFormik({
      validateOnChange: false,
      initialValues,
      validationSchema: validationSchema(t),
      onSubmit,
      enableReinitialize: true,
    });

  const handleUpload = async (e) => {
    const image = e.target.files[0];
    if (!image) return;
    if (IMAGE_TYPE_UPLOAD.includes(image?.type)) {
      if (image.size > IMAGE_MAX_SIZE) {
        showToastError("messages.MSG-USP014");
        return;
      }

      setIsLoadingImage(true);
      const res = await apiUploadImage(image);
      setIsLoadingImage(false);
      setFieldValue("thumbnail", res?.data?.url || "");
    } else {
      showToastError("messages.MSG-USP013");
    }
  };

  const handleDelete = () => {
    if (!data) return;
    actUpdateStatusArticles(
      {
        news_ids: [data.id],
        type: ARTICLE_LIST_OPTIONS_VALUE.DELETE,
      },
      () => {
        actGetDetailArticlesReset();
        closeDeleteModal();
        backToList();
      }
    );
  };

  const openDeleteModal = () => {
    setIsOpenDelete(true);
  };

  const closeDeleteModal = () => {
    setIsOpenDelete(false);
  };

  const handleDeleteImage = () => {
    setFieldValue("thumbnail", "");
  };

  return (
    <div className="article-create-container main-content">
      <div className="root-right-header">
        <div className="screen-name">{t("admin_role")}</div>
      </div>

      <div className="nav">
        <Flex gap="large" wrap="wrap" align="center">
          <div className="screen-title">
            <LeftOutlined onClick={backToList} />
            {isEdit ? t("articles.form.editTitle") : t("articles.form.createTitle")}
          </div>
        </Flex>

        <Row className="article-form" gutter={[16, 16]}>
          {/* Bên trái - nội dung chính */}
          <Col xs={24} md={16}>
            <div className="article-input">
              <div className="input-title">{t("articles.form.title")}</div>
              <InputComponent
                placeholder={t("articles.form.title")}
                name="subject"
                value={values.subject}
                error={errors?.subject}
                onChange={handleChange}
              />
            </div>

            <div style={{ marginTop: "10px" }}>
              <CKEditorCustom
                title={t("articles.form.content")}
                name="content"
                placeholder={t("articles.form.content")}
                value={values.content}
                error={errors?.content_no_html || errors?.content}
                onChange={(_, editor) => {
                  setTimeout(() => {
                    const wc = editor;
                    const text = wc.sourceElement.innerText.replaceAll(
                      `image widget. Press Enter to type after or press Shift + Enter to type before the widget`,
                      ""
                    );
                    setFieldValue("content_no_html", text);
                  }, 1);
                  setFieldValue("content", editor.getData());
                }}
              />
            </div>
          </Col>

          {/* Bên phải - Public + thumbnail */}
          <Col xs={24} md={8} style={{ paddingRight: "30px" }}>
            <div className="public-session">
              <RadioButton
                title={t("articles.form.publicPrivate")}
                options={[
                  {
                    title: t("articles.form.public"),
                    value: ARTICLE_LIST_OPTIONS_VALUE.PUBLISH,
                  },
                  {
                    title: t("articles.form.private"),
                    value: ARTICLE_LIST_OPTIONS_VALUE.HIDDEN,
                  },
                ]}
                name="public"
                value={values.public}
                onChange={handleChange}
              />

              <div className="time-section">
                <div className="time-section-title">
                  {t(`articles.form.publicDate`)}
                </div>
                <DatePickerComponent
                  prefix="calendar-gray.png"
                  placeholder={"YYYY/MM/DD"}
                  format="YYYY/MM/DD"
                  name="date"
                  value={values.date}
                  error={errors?.date}
                  onChange={(e) => setFieldValue("date", e)}
                />
                <DatePickerComponent
                  prefix="clock-gray.png"
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={1}
                  placeholder={"HH:MM"}
                  dateFormat="hh:mm"
                  name="time"
                  value={values.time}
                  error={errors?.time}
                  onChange={(e) => setFieldValue("time", e)}
                />
              </div>
            </div>

            <div className="image-upload-section">
              <div className="image-title">{t(`articles.form.thumbnail`)}</div>
              <div className="image-upload">
                <img
                  src={
                    isLoadingImage
                      ? "/icons/loading.gif"
                      : values?.thumbnail
                        ? values?.thumbnail
                        : "/icons/image.png"
                  }
                  alt=""
                />
              </div>
              <div className="image-button">
                <div
                  onClick={() => {
                    if (isLoadingImage) return;
                    imgRef.current?.click();
                  }}
                >
                  {t(`articles.form.edit`)}
                </div>
                <input
                  ref={imgRef}
                  hidden
                  type="file"
                  accept={IMAGE_TYPE_UPLOAD.join()}
                  onChange={(e) => handleUpload(e)}
                />
                <div onClick={handleDeleteImage}>
                  {t(`articles.form.delete`)}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Button row */}
        <Row gutter={[16, 16]} className="help-button" style={{ marginTop: 24 }}>
          <Col xs={24} md={8}>
            <Button
              text={t("articles.form.save")}
              width="100%"
              height={40}
              type="button"
              style={{
                fontSize: 20,
                fontWeight: 900,
                borderRadius: 60,
              }}
              className="save-button action-btn"
              onClick={handleSubmit}
              disabled={!values.subject || !values.content}
            />
          </Col>

          <Col xs={24} md={12}>
            <Button
              text={t("articles.form.deleteArticle")}
              width="100%"
              height={40}
              background="#FF3131"
              color="#FFFFFF"
              onClick={openDeleteModal}
              type="button"
              style={{
                fontSize: 20,
                fontWeight: 900,
                borderRadius: 60,
              }}
              disabled={!isEdit}
              className={'action-btn'}
            />
          </Col>
        </Row>
      </div>

      {/* Popup confirm delete */}
      <PopupConfirm
        open={isOpenDelete}
        onCancelText={t("button.cancel")}
        onOkText={t("button.delete")}
        content={t("confirm_delete")}
        onOk={handleDelete}
        onCancel={closeDeleteModal}
        onOkColor="#FF3131"
      />

      {(isLoading || isLoadingCreate || isLoadingUpdate || isLoadingUpdateStatus) && <Loading />}
    </div>
  );

};

export default ArticleForm;
